import React, { useState, useEffect } from 'react';
import { Bot, RefreshCw, AlertTriangle } from 'lucide-react';
import { performMarketAnalysis } from '../lib/api/market';
import TrendAnalysis from './TrendAnalysis';
import { useLanguage } from '../lib/i18n';
import { supabase } from '../lib/supabase';

// Cryptocurrency icons from trusted CDN
const CRYPTO_ICONS: Record<string, string> = {
  'BTCUSDT': 'https://s2.coinmarketcap.com/static/img/coins/64x64/1.png',
  'ETHUSDT': 'https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png', 
  'XRPUSDT': 'https://s2.coinmarketcap.com/static/img/coins/64x64/52.png',
  'ADAUSDT': 'https://s2.coinmarketcap.com/static/img/coins/64x64/2010.png',
  'SOLUSDT': 'https://s2.coinmarketcap.com/static/img/coins/64x64/5426.png',
  'DOGEUSDT': 'https://s2.coinmarketcap.com/static/img/coins/64x64/74.png'
};

export default function AIAnalysis() {
  const [analyses, setAnalyses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [aiSettings, setAiSettings] = useState<any>(null);
  const { t } = useLanguage();

  useEffect(() => {
    loadAISettings();
  }, []);

  useEffect(() => {
    if (aiSettings) {
      loadAnalyses();
    }
  }, [aiSettings]);

  const loadAISettings = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('ai_settings')
        .eq('id', 1)
        .single();

      if (error) throw error;
      setAiSettings(data.ai_settings);
    } catch (error) {
      console.error('Error loading AI settings:', error);
    }
  };

  const loadAnalyses = async () => {
    setLoading(true);
    try {
      const symbols = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'ADAUSDT'];
      const results = await Promise.all(
        symbols.map(symbol => performMarketAnalysis(symbol, aiSettings))
      );
      setAnalyses(results);
    } catch (error) {
      console.error('Error loading market analyses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalyses();
    setRefreshing(false);
  };

  const getTrendColor = (trend: string) => {
    switch (trend.toLowerCase()) {
      case 'bullish':
        return 'text-green-400';
      case 'bearish':
        return 'text-red-400';
      default:
        return 'text-yellow-400';
    }
  };

  // Convert confidence to AIX score (-100 to +100)
  const getAIXScore = (trend: string, confidence: number): number => {
    const baseScore = confidence - 50; // Convert 0-100 to -50 to +50
    const multiplier = trend.toLowerCase() === 'bullish' ? 2 : trend.toLowerCase() === 'bearish' ? -2 : 0;
    return Math.round(baseScore * multiplier);
  };

  const renderAIXScore = (trend: string, confidence: number) => {
    const score = getAIXScore(trend, confidence);
    const color = score > 0 ? 'text-green-400' : score < 0 ? 'text-red-400' : 'text-yellow-400';
    
    return (
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-300">AIX Score</span>
          <span className={`text-lg font-bold ${color}`}>
            {score > 0 ? '+' : ''}{score}%
          </span>
        </div>
        <div className="relative h-2 rounded-full overflow-hidden bg-gradient-to-r from-red-500 via-yellow-500 to-green-500">
          <div 
            className="absolute top-0 bottom-0 left-1/2 w-px bg-white"
            style={{ transform: 'translateX(-50%)' }}
          />
          <div
            className="absolute top-0 bottom-0 bg-black/50"
            style={{ 
              left: `${((score + 100) / 2)}%`,
              width: '4px',
              transform: 'translateX(-50%)'
            }}
          />
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-red-400">Sell 100%</span>
          <span className="text-white">Hold</span>
          <span className="text-green-400">Buy 100%</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bot className="h-6 w-6 text-cyan-400" />
          <h2 className="text-xl font-semibold text-white">{t('aiAnalysis.title')}</h2>
        </div>
        <div className="flex items-center space-x-4">
          <p className="text-sm text-gray-400">
            {t('aiAnalysis.lastUpdated')}: {new Date().toLocaleTimeString()}
          </p>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`p-2 rounded-full hover:bg-blue-800/50 transition-colors ${
              refreshing ? 'animate-spin' : ''
            }`}
          >
            <RefreshCw className="h-5 w-5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Warning Message */}
      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start space-x-3">
        <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-300">
          <p className="font-medium text-yellow-500 mb-1">{t('aiAnalysis.disclaimer.title')}</p>
          <p>{t('aiAnalysis.disclaimer.content')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {analyses.map((analysis) => (
          <div
            key={analysis.symbol}
            className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <img
                  src={CRYPTO_ICONS[analysis.symbol]}
                  alt={analysis.symbol}
                  className="h-8 w-8"
                  onError={(e) => {
                    e.currentTarget.src = 'https://s2.coinmarketcap.com/static/img/coins/64x64/1.png';
                  }}
                />
                <div>
                  <h3 className="text-lg font-semibold text-white">{analysis.symbol}</h3>
                  <p className={`text-sm font-medium capitalize ${getTrendColor(analysis.trend)}`}>
                    {analysis.trend} ({analysis.confidence}% confidence)
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* AIX Score */}
              {renderAIXScore(analysis.trend, analysis.confidence)}

              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">{t('aiAnalysis.signals')}</h4>
                <ul className="space-y-2">
                  {analysis.signals.map((signal: string, index: number) => (
                    <li key={index} className="text-sm text-gray-400">
                      • {signal}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">{t('aiAnalysis.prediction')}</h4>
                <p className="text-sm text-gray-300">{analysis.prediction}</p>
              </div>

              <TrendAnalysis
                symbol={analysis.symbol}
                shortTerm={analysis.shortTerm}
                midTerm={analysis.midTerm}
                longTerm={analysis.longTerm}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
